import { useEffect, useRef } from 'react';
import type { Colors } from '../theme';

export type PlaylistItem = {
  videoId: string;
  title: string;
  lengthText: string;
  thumbnail: string;
};

export type ItemStatus = 'idle' | 'converting' | 'ready' | 'error';

export type ItemResult = {
  status: ItemStatus;
  url?: string;
  error?: string;
};

type Props = {
  items: PlaylistItem[];
  selected: Set<string>;
  results: Record<string, ItemResult>;
  c: Colors;
  mobile: boolean;
  format: string;
  onToggle: (videoId: string) => void;
  onToggleAll: () => void;
  onRetry: (videoId: string) => void;
};

function StatusCell({ r, c, format, onRetry, fs }: {
  r: ItemResult | undefined; c: Colors; format: string; onRetry: () => void; fs: number;
}) {
  if (!r || r.status === 'idle') return <span style={{ color: c.muted, fontSize: fs }}>—</span>;
  if (r.status === 'converting') return <span style={{ color: c.muted, fontSize: fs }}>converting…</span>;
  if (r.status === 'ready') {
    return (
      <a
        href={r.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: c.fg, fontSize: fs, textDecoration: 'underline', whiteSpace: 'nowrap' }}
      >
        ↓ {format.toUpperCase()}
      </a>
    );
  }
  return (
    <button
      onClick={onRetry}
      title={r.error}
      style={{
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        color: c.accent, fontSize: fs, fontFamily: 'inherit', textDecoration: 'underline',
      }}
    >
      failed · retry
    </button>
  );
}

export default function PlaylistTable({
  items, selected, results, c, mobile, format, onToggle, onToggleAll, onRetry,
}: Props) {
  const allRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (allRef.current) {
      allRef.current.indeterminate = selected.size > 0 && selected.size < items.length;
    }
  }, [selected.size, items.length]);

  // Mobile gets a tighter type scale; titles scroll sideways rather than
  // truncating, so a long name is still readable on a narrow screen.
  const fs = mobile ? 12 : 13;
  const rowBase: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: mobile ? '20px 1fr auto' : '24px 32px 1fr 64px 96px',
    alignItems: 'center',
    gap: mobile ? 8 : 10,
    padding: mobile ? '8px 2px' : '10px 4px',
    borderBottom: `1px solid ${c.hair}`,
    fontSize: mobile ? 13 : 14,
  };

  return (
    <div style={{ width: '100%', marginTop: 24 }}>
      <label style={{
        ...rowBase,
        gridTemplateColumns: mobile ? '20px 1fr' : '24px 1fr',
        cursor: 'pointer', color: c.muted, fontSize: fs,
      }}>
        <input
          ref={allRef}
          type="checkbox"
          checked={selected.size === items.length && items.length > 0}
          onChange={onToggleAll}
          style={{ accentColor: c.fg, width: mobile ? 15 : 16, height: mobile ? 15 : 16, cursor: 'pointer' }}
        />
        <span>Select all · {selected.size} of {items.length} selected</span>
      </label>

      <div style={{ maxHeight: mobile ? 'none' : 420, overflowY: 'auto' }}>
        {items.map((item, i) => {
          const isSelected = selected.has(item.videoId);
          return (
            <div key={item.videoId} style={{ ...rowBase, opacity: isSelected ? 1 : 0.5 }}>
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggle(item.videoId)}
                style={{ accentColor: c.fg, width: mobile ? 15 : 16, height: mobile ? 15 : 16, cursor: 'pointer' }}
              />
              {!mobile && <span style={{ color: c.muted, fontSize: fs }}>{i + 1}</span>}
              <span className="row-title" style={{
                overflowX: 'auto', whiteSpace: 'nowrap', minWidth: 0,
              }} title={item.title}>
                {mobile && <span style={{ color: c.muted, marginRight: 6 }}>{i + 1}</span>}
                {item.title}
              </span>
              {!mobile && (
                <span style={{ color: c.muted, fontSize: fs }}>{item.lengthText}</span>
              )}
              <span style={{ textAlign: 'right' }}>
                <StatusCell
                  r={results[item.videoId]}
                  c={c}
                  format={format}
                  fs={fs}
                  onRetry={() => onRetry(item.videoId)}
                />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
